/**
 * Holds the Superblocks JWT for the current session, and the mint that can
 * replace it.
 *
 * Shared by both trees. It lived as two forks for a long time, and they drifted:
 * the dashboard's was missing `setFetchToken` and `refetchToken`, so nothing
 * there could replace the token once the provider token inside it went stale,
 * and an SSO re-auth prompt came back on every datasource execution until the
 * page was reloaded.
 *
 * Each tree re-exports this through its own `auth/token` alias, so existing
 * importers are unchanged.
 */
class TokenProvider {
  private tokenType?: "embed" | "superblocks";
  private token?: string;
  private _fetchToken?: (() => Promise<void>) | (() => void);
  private static instance: TokenProvider;

  private constructor() {}

  setTokenType(tokenType: "embed" | "superblocks") {
    this.tokenType = tokenType;
  }

  setToken(token: string) {
    this.token = token;
  }

  getToken(): string | undefined {
    return this.token;
  }

  clearToken() {
    this.token = undefined;
    this.tokenType = undefined;
  }

  getTokenType() {
    return this.tokenType;
  }

  /**
   * Registered by `useJwt` so the rest of the tree can replace the token
   * without owning the mint.
   */
  setFetchToken(fetchToken: (() => Promise<void>) | (() => void) | undefined) {
    this._fetchToken = fetchToken;
  }

  async refetchToken() {
    if (!this._fetchToken) {
      return;
    }
    await this._fetchToken();
  }

  public static get Instance() {
    return this.instance || (this.instance = new this());
  }
}

export default TokenProvider.Instance;
